'use client'

import { useModal } from '@/contexts/ModalContext'
import { useLocation } from '@/hooks/useLocation'
import { RiMapPinLine } from '@remixicon/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768
const STAYS_ACCENT = '#80C7E2'
const STAYS_ACCENT_SOFT = '#F2FBFE'
const STAYS_ACCENT_SOFT_HOVER = '#E8F7FC'
const STAYS_TEXT_DARK = '#000D33'
const STAYS_TEXT_MUTED = '#55656F'
const PANEL_EASE = [0.22, 1, 0.36, 1]
const PANEL_EXIT_EASE = [0.4, 0, 1, 1]
const PANEL_LAYOUT_TRANSITION = {
  type: 'spring',
  stiffness: 420,
  damping: 34,
  mass: 0.82,
}

const desktopPanelMotion = {
  initial: { opacity: 0, y: 12, scale: 0.96, filter: 'blur(8px)' },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: 0.24, ease: PANEL_EASE },
  },
  exit: {
    opacity: 0,
    y: 8,
    scale: 0.98,
    filter: 'blur(4px)',
    transition: { duration: 0.16, ease: PANEL_EXIT_EASE },
  },
}

const mobileBackdropMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.18, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: 0.14, ease: 'easeIn' } },
}

const mobileSheetMotion = {
  initial: { opacity: 0, y: 36, scale: 0.98 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.24, ease: PANEL_EASE },
  },
  exit: {
    opacity: 0,
    y: 24,
    scale: 0.99,
    transition: { duration: 0.16, ease: PANEL_EXIT_EASE },
  },
}

const LocationDropdown = ({
  value = '',
  placeholder = 'Where to?',
  className = '',
  dropdownPosition = 'top-[53px]',
  onChange,
  onOpenChange,
  dismissSignal,
  desktopPanelLayoutId,
  mobileSheetLayoutId,
  showHeader = true,
}) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [hoveredOptionKey, setHoveredOptionKey] = useState(null)
  const locationRef = useRef(null)

  // Uncomment if you want to use modal
  // const { handleOpen } = useModal()

  const handleClick = () => {
    // Simply toggle the dropdown
    setShowDropdown((prev) => !prev)

    // Uncomment if you want to open modal along with dropdown
    // handleOpen()
  }

  const { cities, loading, isError } = useLocation()

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    onOpenChange?.(showDropdown)
  }, [onOpenChange, showDropdown])

  useEffect(() => {
    setShowDropdown(false)
  }, [dismissSignal])

  const getPanelStyle = () => ({
    scrollbarWidth: 'none',
    overflowY: 'auto',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFEFF 100%)',
    border: '1px solid #D8F1F8',
    boxShadow: '0 24px 56px rgba(10, 28, 44, 0.14)',
  })

  const getOptionStyle = (optionKey, cityName) => {
    const isSelected = value === cityName
    const isHovered = hoveredOptionKey === optionKey

    return {
      background: isSelected
        ? '#F2FBFE'
        : isHovered
          ? '#F8FCFE'
          : '#FFFFFF',
      borderColor: isSelected ? '#B7E2EF' : isHovered ? '#E1F0F6' : '#EDF5F8',
      boxShadow: isSelected
        ? '0 8px 18px rgba(128, 199, 226, 0.12)'
        : isHovered
          ? '0 4px 12px rgba(10, 28, 44, 0.04)'
          : 'none',
      transform: isHovered ? 'translateY(-0.5px)' : 'translateY(0)',
      transition: 'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
    }
  }

  const getIconTileStyle = (optionKey, cityName) => {
    const isSelected = value === cityName
    const isHovered = hoveredOptionKey === optionKey

    return {
      backgroundColor: isSelected || isHovered ? '#FFFFFF' : '#F7FBFD',
      borderColor: isSelected ? '#D0ECF6' : isHovered ? '#DCEFF6' : '#E6F1F6',
      color: STAYS_TEXT_DARK,
      boxShadow: isSelected
        ? '0 8px 16px rgba(128, 199, 226, 0.14)'
        : isHovered
          ? '0 6px 12px rgba(10, 28, 44, 0.04)'
          : 'none',
      transition: 'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
    }
  }

  const desktopPanelAnimationProps = desktopPanelLayoutId
    ? {
        layout: true,
        transition: { layout: PANEL_LAYOUT_TRANSITION },
      }
    : {
        initial: desktopPanelMotion.initial,
        animate: desktopPanelMotion.animate,
        exit: desktopPanelMotion.exit,
      }

  const mobileSheetAnimationProps = mobileSheetLayoutId
    ? {
        layout: true,
        transition: { layout: PANEL_LAYOUT_TRANSITION },
      }
    : {
        initial: mobileSheetMotion.initial,
        animate: mobileSheetMotion.animate,
        exit: mobileSheetMotion.exit,
      }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        locationRef.current &&
        !locationRef.current.contains(event.target) &&
        !(event.target instanceof Element && event.target.closest('[data-location-mobile-portal]'))
      ) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      // Use timeout to avoid immediate close from the same click
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 0)
      return () => {
        clearTimeout(timer)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  // Lock body scroll when mobile bottom sheet is open
  useEffect(() => {
    if (showDropdown && isMobile()) {
      document.body.classList.add('bottom-sheet-open')
    } else {
      document.body.classList.remove('bottom-sheet-open')
    }
    return () => document.body.classList.remove('bottom-sheet-open')
  }, [showDropdown])

  return (
    <div className={`relative ${className}`} ref={locationRef}>
      <div className={`cursor-pointer select-none font-medium`} onClick={handleClick}>
        {value ? (
          <span className='text-[14px] font-semibold text-[#191919] font-poppins'>
            {value}
          </span>
        ) : (
          <span className='md:text-[14px] text-[14px] font-medium text-[#898989] font-poppins'>
            {placeholder}
          </span>
        )}
      </div>

      <AnimatePresence initial={false}>
        {showDropdown && (
        <>
          {/* Desktop dropdown */}
          <motion.div
            layoutId={desktopPanelLayoutId}
            className={`hidden md:block absolute ${dropdownPosition} bg-white shadow-xl rounded-2xl border border-[#D8E3FF] z-[99999] max-h-[340px] w-[374px] px-3 py-0`}
            style={getPanelStyle()}
            {...desktopPanelAnimationProps}
          >
            {showHeader && (
              <div
                className='border-b px-1 pt-3 pb-3'
                style={{ borderColor: '#EAF6FB' }}
              >
                <div
                  className='text-[12px] font-semibold'
                  style={{ color: STAYS_TEXT_DARK }}
                >
                  Destinations
                </div>
                <div className='text-[13px] leading-5' style={{ color: STAYS_TEXT_MUTED }}>
                  Choose where you want to stay
                </div>
              </div>
            )}
            {loading ? (
              <div className='p-4 text-[15px] font-medium font-sans' style={{ color: STAYS_TEXT_MUTED }}>
                Loading...
              </div>
            ) : isError ? (
              <div className='p-4 text-[15px] font-medium text-red-500 font-sans'>
                Failed to load locations
              </div>
            ) : cities?.data?.length > 0 ? (
              <div className='space-y-0'>
                {cities?.data?.map((option, i) => {
                  const optionKey = `${option?.id}-${i}`
                  const isSelected = value === option?.city_name

                  return (
                    <button
                      key={optionKey}
                      type='button'
                      className='flex min-h-[70px] w-full cursor-pointer items-center gap-3 rounded-[14px] px-0 py-3 text-left transition-all duration-180'
                      style={getOptionStyle(optionKey, option?.city_name)}
                      onMouseEnter={() => setHoveredOptionKey(optionKey)}
                      onMouseLeave={() => setHoveredOptionKey((current) => (current === optionKey ? null : current))}
                      onClick={() => {
                        setShowDropdown(false)
                        onChange(option?.city_name)
                      }}
                    >
                      <div
                        className='flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px] border'
                        style={getIconTileStyle(optionKey, option?.city_name)}
                      >
                        <RiMapPinLine size={22} />
                      </div>
                      <div className='flex flex-col min-w-0 flex-1'>
                        <span className='truncate font-semibold text-[14px] leading-5 text-[#191919]'>
                          {option?.city_name}
                        </span>
                        <span className='truncate text-[12px] leading-[17px]' style={{ color: STAYS_TEXT_MUTED }}>
                          {option?.title} {option?.country}
                        </span>
                      </div>
                      {isSelected && (
                        <span
                          className='flex-shrink-0 rounded-full px-2 py-[5px] text-[10px] font-semibold'
                          style={{ backgroundColor: '#EAF8FC', color: '#4A9DBD' }}
                        >
                          Selected
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className='p-4 text-[14px] font-medium font-sans' style={{ color: STAYS_TEXT_MUTED }}>
                No locations found
              </div>
            )}
          </motion.div>

          {/* Mobile bottom sheet — portaled to body */}
          {isMounted &&
            createPortal(
              <AnimatePresence initial={false}>
                {showDropdown && (
                  <div className='md:hidden' data-location-mobile-portal>
                    <motion.div
                      className='fixed inset-0 z-[99998]'
                      style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
                      initial={mobileBackdropMotion.initial}
                      animate={mobileBackdropMotion.animate}
                      exit={mobileBackdropMotion.exit}
                      onClick={() => setShowDropdown(false)}
                    />
                    <div className='fixed bottom-0 left-0 right-0 z-[99999]'>
                      <motion.div
                        layoutId={mobileSheetLayoutId}
                        className='bg-white rounded-t-[28px] shadow-2xl max-h-[70vh] flex flex-col'
                        style={{ boxShadow: '0 -12px 36px rgba(10, 28, 44, 0.18)' }}
                        {...mobileSheetAnimationProps}
                      >
                        <div className={`flex justify-end px-4 pt-4 ${showHeader ? 'pb-3 border-b' : 'pb-2'}`} style={showHeader ? { borderColor: '#EAF6FB' } : undefined}>
                          <button
                            type='button'
                            onClick={() => setShowDropdown(false)}
                            className='text-[#898989] text-sm font-medium cursor-pointer'
                          >
                            Close
                          </button>
                        </div>
                        <div
                          className='overflow-y-auto px-4 py-0'
                          style={{ scrollbarWidth: 'none' }}
                        >
                          {loading ? (
                            <div className='p-4 text-[15px] font-medium font-sans' style={{ color: STAYS_TEXT_MUTED }}>
                              Loading...
                            </div>
                          ) : isError ? (
                            <div className='p-4 text-[15px] font-medium text-red-500 font-sans'>
                              Failed to load locations
                            </div>
                          ) : cities?.data?.length > 0 ? (
                            <div className='space-y-0'>
                              {cities?.data?.map((option, i) => {
                                const optionKey = `mobile-${option?.id}-${i}`
                                const isSelected = value === option?.city_name

                                return (
                                  <button
                                    key={optionKey}
                                    type='button'
                                    className='flex min-h-[70px] w-full cursor-pointer items-center gap-3 rounded-[14px] px-0 py-3 text-left transition-all duration-180'
                                    style={getOptionStyle(optionKey, option?.city_name)}
                                    onMouseEnter={() => setHoveredOptionKey(optionKey)}
                                    onMouseLeave={() => setHoveredOptionKey((current) => (current === optionKey ? null : current))}
                                    onClick={() => {
                                      setShowDropdown(false)
                                      onChange(option?.city_name)
                                    }}
                                  >
                                    <div
                                      className='flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px] border'
                                      style={getIconTileStyle(optionKey, option?.city_name)}
                                    >
                                      <RiMapPinLine size={22} />
                                    </div>
                                    <div className='flex flex-col min-w-0 flex-1'>
                                      <span className='truncate font-semibold text-[14px] leading-5 text-[#191919]'>
                                        {option?.city_name}
                                      </span>
                                      <span className='truncate text-[12px] leading-[17px]' style={{ color: STAYS_TEXT_MUTED }}>
                                        {option?.title} {option?.country}
                                      </span>
                                    </div>
                                    {isSelected && (
                                      <span
                                        className='flex-shrink-0 rounded-full px-2 py-[5px] text-[10px] font-semibold'
                                        style={{ backgroundColor: '#EAF8FC', color: '#4A9DBD' }}
                                      >
                                        Selected
                                      </span>
                                    )}
                                  </button>
                                )
                              })}
                            </div>
                          ) : (
                            <div className='p-4 text-[14px] font-medium font-sans' style={{ color: STAYS_TEXT_MUTED }}>
                              No locations found
                            </div>
                          )}
                        </div>
                      </motion.div>
                    </div>
                  </div>
                )}
              </AnimatePresence>,
              document.body,
            )}
        </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LocationDropdown
